// src/pages/ServiceVaccinePage.js
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import SearchIcon from "@mui/icons-material/Search";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import QrReader from "react-qr-scanner";
import * as services from "../../services/services";
import * as dropdown from "../../services/dropdown_service";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { useState, useEffect, useCallback, useRef } from "react";
import { transformPet } from "../../utils/reModelPet";
import { extractPetIdFromQrText } from "../../utils/qrPetId";
import {
  buildLocalOwnerFromBowPet,
  syncBowOwnerToLocalPet,
} from "../../utils/bowOwnerSync";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { Close, Folder } from "@mui/icons-material";
import IMG from "../../images/istockphoto.jpg";
import { formatDate } from "../../utils/formatDate";
import Webcam from "react-webcam";

const ServiceVaccinePage = () => {
  // --- state ---
  const accessToken = localStorage.getItem("accessToken");
  const navigate = useNavigate();
  const location = useLocation();
  const decoded = jwtDecode(accessToken);
  const webcamRef = useRef(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [cameraIndex, setCameraIndex] = useState(null);
  const [petIdInput, setPetIdInput] = useState("");
  const [pet, setPet] = useState(null);
  const [searched, setSearched] = useState(false);
  const [vaccine_dropdown, setVaccineDropdown] = useState([]);
  const today = new Date().toISOString().split("T")[0];

  const [vaccineList, setVaccineList] = useState([
    {
      date: today,
      image: null,
      vaccineName: "",
      lotNo: "",
      expDate: "",
      nextDate: "",
    },
  ]);
  const [detail, setDetail] = useState([]); // ข้อมูลหมอคนเดียว
  const [vet, setVet] = useState({
    name: "",
    lastname: "",
    hospital: "",
    license_no: "",
    note: "",
  });
  const [petSource, setPetSource] = useState("external");
  const [internalPetList, setInternalPetList] = useState([]);
  const [openQrDialog, setOpenQrDialog] = useState(false);

  const openQrScanner = () => {
    setOpenQrDialog(true);
  };

  // --- ค้นหาสัตว์เลี้ยง ---
  const searchPet = async (scannedId = null) => {
    const idToSearch = extractPetIdFromQrText(
      typeof scannedId === "string" ? scannedId : petIdInput,
    );
    if (!idToSearch) return;
    try {
      const res = await services.getPetBowById(idToSearch);
      setPet({
        ...res.data,
        owner: res.data.owners?.[0] || null,
      });
      fetchVaccine(res.data.type_id);
      if (typeof scannedId === "string") setPetIdInput(idToSearch);
    } catch (err) {
      setPet(null);
    }
    setSearched(true);
  };

  const searchInternalPet = async (keyword) => {
    try {
      const res = await services.getPetsBykeyword(keyword);
      setInternalPetList(res.data.data);
    } catch (error) {
      setInternalPetList([]);
    }
  };
  // --- โหลด dropdown วัคซีน ---
  const fetchVaccine = async (type_id) => {
    try {
      const res = await dropdown.getVaccine(type_id);
      setVaccineDropdown(res.data.results);
    } catch (err) {
      console.error(err);
    }
  };

  // --- เพิ่มรายการวัคซีน ---
  const addVaccineList = () => {
    setVaccineList([
      ...vaccineList,
      {
        date: "",
        image: null,
        vaccineName: "",
        lotNo: "",
        expDate: "",
        nextDate: "",
      },
    ]);
  };

  const captureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();

    if (imageSrc && cameraIndex !== null) {
      const newList = [...vaccineList];
      newList[cameraIndex].image = imageSrc;
      setVaccineList(newList);
    }

    setOpenCamera(false);
    setCameraIndex(null);
  };

  // --- ดึงข้อมูลหมอคนเดียว ---
  const fetchData = useCallback(async () => {
    try {
      const res = await services.getUserDetail();
      if (res.data.success) {
        setDetail(res.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- fill vet auto ---
  useEffect(() => {
    const doctor = detail;
    setVet({
      doctor_id: doctor.id,
      name: doctor.name,
      lastname: doctor.lastname,
      hospital: doctor?.hospital?.name,
      license_no: doctor?.license_no,
      note: "",
    });
  }, [detail]);
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      if (!file) return resolve(null);

      // ถ้าเป็น string อยู่แล้ว ให้ return ทันที
      if (typeof file === "string") return resolve(file);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1]; // ตัด prefix ออก
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });

  // --- บันทึกการฉีดวัคซีน ---
  const saveVaccine = async () => {
    try {
      if (!pet) {
        alert("กรุณาเลือกสัตว์ก่อนบันทึก");
        return;
      }

      // =========================
      // VALIDATE
      // =========================
      const errors = [];

      vaccineList.forEach((v, idx) => {
        const row = idx + 1;

        if (!v.vaccineName || String(v.vaccineName).trim() === "") {
          errors.push(`รายการ ${row}: กรุณาเลือกชื่อวัคซีน`);
        }

        if (!v.date || String(v.date).trim() === "") {
          errors.push(`รายการ ${row}: กรุณาเลือกวันที่ฉีด`);
        }

        if (!v.lotNo || String(v.lotNo).trim() === "") {
          errors.push(`รายการ ${row}: กรุณากรอก Lot No.`);
        }

        if (!v.expDate || String(v.expDate).trim() === "") {
          errors.push(`รายการ ${row}: กรุณาเลือกวันหมดอายุ`);
        }

        if (!v.nextDate || String(v.nextDate).trim() === "") {
          errors.push(`รายการ ${row}: กรุณาเลือกวันที่นัดครั้งถัดไป`);
        }
      });

      if (errors.length) {
        alert(errors.join("\n"));
        return;
      }

      // =========================
      // BOW ITEMS
      // =========================
      const items = await Promise.all(
        vaccineList.map(async (v) => ({
          product: v.vaccineName,

          lot_number: v.lotNo,

          expiration_date: v.expDate,

          appointed_date: v.nextDate,

          photos: [
            {
              photo: v.image ? await toBase64(v.image) : null,

              is_label: true,
            },
          ],
        })),
      );

      const isLinkedToBow =
        petSource === "external" || (petSource === "internal" && pet.petCode);

      // =========================
      // SEND TO BOW
      // =========================
      if (isLinkedToBow) {
        const payload = {
          pet: petSource === "external" ? pet.id : pet.petCode,

          date: vaccineList[0].date,

          note: vet.note,

          items,

          vet_name: vet.name + " " + vet.lastname,

          vet_license_number: vet.license_no,

          vet_hospital_name: vet.hospital,

          created_hospital_id: decoded.hospitalId,
        };

        console.log("BOW PAYLOAD =>", payload);

        const res = await services.putVaccineRecordsBow(payload);

        if (res.status === 201) {
          // =========================
          // UPDATE IMAGE FROM BOW
          // =========================
          let updatedVaccineList = vaccineList;

          if (res.data.items) {
            updatedVaccineList = vaccineList.map((v, index) => {
              const backendItem = res.data.items[index];

              return {
                ...v,

                image: backendItem?.photos?.[0]?.photo || v.image,

                id: backendItem?.id || v.id,
              };
            });

            setVaccineList(updatedVaccineList);
          }

          // =========================
          // LOCAL VACCINE DATA
          // =========================
          const vaccines = updatedVaccineList.map((v) => ({
            vetId: decoded.id,

            product: String(v.vaccineName),

            lotNumber: v.lotNo || null,

            expirationDate: v.expDate ? new Date(v.expDate) : null,

            appointedDate: v.nextDate ? new Date(v.nextDate) : null,

            photo: v.image || null,
          }));

          // =========================
          // OWNER FROM BOW
          // =========================
          const bowOwner = buildLocalOwnerFromBowPet(pet);

          // =========================
          // PET DATA
          // =========================
          let petData =
            petSource === "external"
              ? {
                  ...transformPet(pet, decoded.hospitalId),

                  ownerId: null,

                  owner: bowOwner,
                }
              : {
                  ...pet,
                  hospitalId: decoded.hospitalId,
                };

          console.log("LOCAL PET DATA =>", petData);

          // =========================
          // CREATE LOCAL TREATMENT
          // =========================
          const localPayload = {
            pet: petData,

            treatments: [
              {
                serviceType: "vaccine",

                serviceDate: new Date(vaccineList[0].date),

                handledById: decoded.id,

                hospitalId: decoded.hospitalId,

                note: vet.note,
              },
            ],

            vaccines,
          };

          console.log("LOCAL PAYLOAD =>", localPayload);

          const resTreat = await services.createTreatments(localPayload);

          // =========================
          // CREATE APPOINTMENT
          // =========================
          if (resTreat.data.success) {
            if (petSource === "external") {
              try {
                await syncBowOwnerToLocalPet({
                  bowPet: pet,
                  treatmentResult: resTreat,
                });
              } catch (err) {
                console.error("Sync BOW owner failed:", err);
              }

              try {
                const selectedVaccine = vaccine_dropdown.find(
                  (v) => v.value === vaccineList[0].vaccineName,
                );

                const appointPayload = {
                  pet: pet.id,

                  type: "vaccine",

                  topic: selectedVaccine?.label || "วัคซีน",

                  type_setting: "ฉีดวัคซีน",

                  product: vaccineList[0].vaccineName,

                  created_hospital: decoded.hospitalId,

                  detail: vet.note || "",

                  location: vet.hospital || "",

                  appoint_at: vaccineList[0].nextDate,

                  notify_at: vaccineList[0].nextDate,

                  custom_type: 2,

                  show_datetime: false,

                  public: true,
                };

                console.log("APPOINTMENT =>", appointPayload);

                await services.createAppointmentBow(appointPayload);
              } catch (err) {
                console.error("Create BOW appointment failed:", err);
              }
            }

            alert("บันทึกสำเร็จ");

            navigateAfterSave(resTreat.data.data.treatmentIds);
          }
        }
      }

      // =========================
      // LOCAL ONLY
      // =========================
      else {
        if (!vaccineList.length) return;

        const vaccinesWithPhoto = await Promise.all(
          vaccineList.map(async (v) => {
            let photoUrl = null;

            if (v.image && v.image instanceof File) {
              const uploadRes = await services.uploadImage(v.image, "vaccine");

              photoUrl = uploadRes?.data?.url || null;
            } else {
              photoUrl = v.image || null;
            }

            return {
              vetId: decoded.id,

              product: String(v.vaccineName),

              lotNumber: v.lotNo || null,

              expirationDate: v.expDate ? new Date(v.expDate) : null,

              appointedDate: v.nextDate ? new Date(v.nextDate) : null,

              photo: photoUrl,
            };
          }),
        );

        const treatmentPayload = {
          pet: {
            ...pet,

            hospitalId: decoded.hospitalId,
          },

          treatments: [
            {
              serviceType: "vaccine",

              serviceDate: new Date(vaccineList[0].date),

              handledById: decoded.id,

              hospitalId: decoded.hospitalId,

              note: vet.note,
            },
          ],

          vaccines: vaccinesWithPhoto,
        };

        console.log("LOCAL ONLY PAYLOAD =>", treatmentPayload);

        const resTreat = await services.createTreatments(treatmentPayload);

        if (resTreat?.data?.success) {
          alert("บันทึกสำเร็จ");

          navigateAfterSave(resTreat.data.data.treatmentIds);
        }
      }
    } catch (err) {
      console.error(err);
      alert("บันทึกไม่สำเร็จ");
    }
  };

  function navigateAfterSave(treatmentIds) {
    if (location.pathname.startsWith("/mobile")) {
      navigate("/mobile", { replace: true });
      return;
    }

    navigate(`/medical_history/${treatmentIds}`);
  }

  const addDays = (dateStr, days) => {
    if (!dateStr || !days) return "";

    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);

    return date.toISOString().split("T")[0]; // yyyy-mm-dd
  };
  const calculateAgeText = (birthdate) => {
    if (!birthdate) return "-";

    const birth = new Date(birthdate);
    const today = new Date();

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();

    if (today.getDate() < birth.getDate()) {
      months -= 1;
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${years} ปี ${months} เดือน`;
  };
  return (
    <>
      <AppBreadcrumbs />
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">บันทึกการฉีดวัคซีน</Typography>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setPet(null);
              setSearched(false);
              setPetIdInput("");
              setVaccineList([
                {
                  date: "",
                  image: null,
                  vaccineName: "",
                  lotNo: "",
                  expDate: "",
                  nextDate: "",
                },
              ]);
            }}
          >
            เคลียร์
          </Button>
        </Box>

        {/* ค้นหาสัตว์เลี้ยง */}
        {!pet && !searched && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              ค้นหาสัตว์เลี้ยง
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TextField
                select
                label="แหล่งข้อมูลสัตว์เลี้ยง"
                value={petSource}
                onChange={(e) => {
                  setPetSource(e.target.value);
                  setPet(null);
                  setSearched(false);
                }}
              >
                <MenuItem value="external">จากระบบ Bow</MenuItem>
                <MenuItem value="internal">สัตว์ในโรงพยาบาล</MenuItem>
              </TextField>
            </Box>
            {petSource === "external" && (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Pet ID หรือ สแกน QR code"
                    fullWidth
                    value={petIdInput}
                    onChange={(e) => setPetIdInput(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <IconButton
                    color="primary"
                    onClick={openQrScanner}
                  >
                    <QrCodeScannerIcon fontSize="large" />
                  </IconButton>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={searchPet}
                    size="large"
                  >
                    ค้นหา
                  </Button>
                </Grid>
              </Grid>
            )}
            {petSource === "internal" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="ค้นหาสัตว์ (ชื่อ / microchip /เบอร์โทรศัพท์เจ้าของ)"
                    fullWidth
                    onChange={(e) => searchInternalPet(e.target.value)}
                  />

                  <Box sx={{ mt: 2 }}>
                    {internalPetList.map((p) => (
                      <Paper
                        key={p.id}
                        elevation={2}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          cursor: "pointer",
                          transition: "0.2s",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            transform: "translateY(-2px)",
                          },
                        }}
                        onClick={() => {
                          setPet(p);
                          fetchVaccine(p.typeId);
                        }}
                      >
                        {/* รูป */}
                        <Box
                          component="img"
                          src={p.image ? p.image : IMG}
                          alt={p.name}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            objectFit: "cover",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        />

                        {/* ข้อมูล */}
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            {p.name}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {p.hospitalPetCode || "-"} • Microchip:{" "}
                            {p.microchipNumber || "-"}
                          </Typography>

                          <Typography variant="body2">
                            เจ้าของ:{" "}
                            {p.owner
                              ? `${p.owner.firstName} ${p.owner.lastName}`
                              : "ไม่มีเจ้าของ"}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                    {internalPetList.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        ไม่พบข้อมูลสัตว์เลี้ยง
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {searched && !pet && (
          <Typography color="error" sx={{ mb: 2 }}>
            ไม่พบข้อมูลสัตว์เลี้ยง กรุณาตรวจสอบ Pet ID หรือ QR code
          </Typography>
        )}

        {/* ข้อมูลสัตว์เลี้ยง */}
        {pet && petSource === "internal" && (
          <>
            <Paper sx={{ p: 2, mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <img
                    src={pet.image ? pet.image : IMG}
                    alt={pet.name}
                    style={{
                      width: pet.image ? "70%" : "100%",
                      borderRadius: 8,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    ชื่อ: {pet.name}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mt: 1 }}
                  >
                    ข้อมูลสัตว์เลี้ยง
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ชนิดสัตว์:
                      </Typography>{" "}
                      {pet.type}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สายพันธุ์:
                      </Typography>{" "}
                      {pet.breed}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        เพศ:
                      </Typography>{" "}
                      {{ m: "ตัวผู้", f: "ตัวเมีย", b: "ไม่ทราบ" }[
                        pet.gender
                      ] || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        วันเกิด:
                      </Typography>{" "}
                      {formatDate(pet.birthdate)}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        อายุ:
                      </Typography>{" "}
                      {calculateAgeText(pet.birthdate)}
                      ปี
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ทำหมัน:
                      </Typography>{" "}
                      {pet.sterilizationDate ? "ทำแล้ว" : "ยัง"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สีตา:
                      </Typography>{" "}
                      {pet.eyeColor}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สีขน:
                      </Typography>{" "}
                      {pet.furColor}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ตำหนิ:
                      </Typography>{" "}
                      {pet.appearanceDetail || "-"}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ไมโครชิป:
                      </Typography>{" "}
                      {pet.microchipNumber || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        กรุ๊ปเลือด:
                      </Typography>{" "}
                      {pet.bloodGroup}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ยาที่แพ้:
                      </Typography>{" "}
                      {pet.drugAllergies || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        อาหารที่แพ้:
                      </Typography>{" "}
                      {pet.foodAllergies || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        โรคประจำตัว:
                      </Typography>{" "}
                      {pet.diseases?.[0] || "-"}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        เจ้าของ:
                      </Typography>{" "}
                      {pet?.owner
                        ? "คุณ " +
                          pet.owner.firstName +
                          " " +
                          pet.owner.lastName
                        : "ไม่มีเจ้าของ"}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {/* ฟอร์มวัคซีน */}
            {vaccineList.map((item, index) => (
              <Paper key={index} sx={{ p: 2, mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  รายละเอียดวัคซีน
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="วันที่ได้รับวัคซีน"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={item.date}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        const date = e.target.value;

                        newList[index].date = date;

                        // หา effective_days จาก vaccine ที่เลือก
                        const selectedVaccine = vaccine_dropdown.find(
                          (v) => v.value === newList[index].vaccineName,
                        );

                        if (selectedVaccine?.context?.effective_days) {
                          newList[index].nextDate = addDays(
                            date,
                            selectedVaccine.context.effective_days,
                          );
                        }

                        setVaccineList(newList);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ mb: 1 }}>
                      แนบรูป Vaccine Sticker*
                    </Typography>
                    <Box
                      sx={{
                        border: "2px dashed #ccc",
                        padding: 4,
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setCameraIndex(index);
                          setOpenCamera(true);
                        }}
                      >
                        <PhotoCamera fontSize="large" />
                      </IconButton>
                      <Typography variant="body2" sx={{ mt: 1, color: "#888" }}>
                        อัพโหลดรูปภาพขนาดไม่เกิน 5MB
                      </Typography>

                      {/* เพิ่มส่วนแสดงรูปทันที */}
                      {item.image && (
                        <Box sx={{ mt: 2 }}>
                          <img
                            src={
                              typeof item.image === "string"
                                ? `data:image/jpeg;base64,${item.image}`
                                : URL.createObjectURL(item.image)
                            }
                            alt="vaccine"
                            style={{
                              width: 100,
                              height: 100,
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      select
                      label="ชื่อวัคซีน / brand name vaccine"
                      fullWidth
                      value={item.vaccineName}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        const vaccineName = e.target.value;

                        newList[index].vaccineName = vaccineName;

                        const selectedVaccine = vaccine_dropdown.find(
                          (v) => v.value === vaccineName,
                        );

                        if (
                          newList[index].date &&
                          selectedVaccine?.context?.effective_days
                        ) {
                          newList[index].nextDate = addDays(
                            newList[index].date,
                            selectedVaccine.context.effective_days,
                          );
                        }

                        setVaccineList(newList);
                      }}
                    >
                      {vaccine_dropdown.map((v) => (
                        <MenuItem key={v.value} value={v.value}>
                          <img
                            src={v?.context?.photo}
                            alt={v.label}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                          />
                          {v.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Lot No."
                      fullWidth
                      value={item.lotNo}
                      required
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].lotNo = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="วันหมดอายุ / EXP date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={item.expDate}
                      required
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].expDate = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      label="วันที่นัดครั้งถัดไป"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={item.nextDate}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].nextDate = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mt: 1, color: "#000000" }}>
                      วันที่ครบระยะเวลาป้องกัน
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: "#888" }}>
                      อ้างอิงจากวันที่ได้รับยา โดยหลังจากนี้ควรได้รับยาอีกครั้ง
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, color: "#ee820e" }}>
                      {item.nextDate}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={addVaccineList}
              sx={{ mb: 4 }}
            >
              เพิ่มรายการ
            </Button> */}

            {/* ฟอร์มหมอ */}
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
              สัตวแพทย์
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="สัตวแพทย์ผู้ให้บริการ"
                  value={`${vet.name} ${vet.lastname}`}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="เลขที่ใบอนุญาต"
                  value={vet.license_no}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ชื่อสถานพยาบาล"
                  value={vet.hospital}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ข้อมูลเพิ่มเติม"
                  value={vet.note}
                  onChange={(e) =>
                    setVet((prev) => ({ ...prev, note: e.target.value }))
                  }
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={saveVaccine}
              >
                บันทึกการฉีดวัคซีน
              </Button>
            </Box>
            <Box sx={{ height: 50 }} />
          </>
        )}
        {pet && petSource === "external" && (
          <>
            <Paper sx={{ p: 2, mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <img
                    src={pet.image ? pet.image : IMG}
                    alt={pet.name}
                    style={{
                      width: pet.image ? "70%" : "100%",
                      borderRadius: 8,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    ชื่อ: {pet.name}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mt: 1 }}
                  >
                    ข้อมูลสัตว์เลี้ยง
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ชนิดสัตว์:
                      </Typography>{" "}
                      {pet.type}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สายพันธุ์:
                      </Typography>{" "}
                      {pet.breed}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        เพศ:
                      </Typography>{" "}
                      {{ m: "ตัวผู้", f: "ตัวเมีย", b: "ไม่ทราบ" }[
                        pet.gender
                      ] || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        วันเกิด:
                      </Typography>{" "}
                      {formatDate(pet.birthdate)}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        อายุ:
                      </Typography>{" "}
                      {calculateAgeText(pet.birthdate)}
                      ปี
                    </Grid>
                    {/* <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ทำหมัน:
                      </Typography>{" "}
                      {pet.sterilization_date ? "ทำแล้ว" : "ยัง"}
                    </Grid> */}
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สีตา:
                      </Typography>{" "}
                      {pet.eye_color}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        สีขน:
                      </Typography>{" "}
                      {pet.fur_color}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ตำหนิ:
                      </Typography>{" "}
                      {pet.appearance_detail || "-"}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ไมโครชิป:
                      </Typography>{" "}
                      {pet.microchip_number || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        กรุ๊ปเลือด:
                      </Typography>{" "}
                      {pet.blood_group}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        ยาที่แพ้:
                      </Typography>{" "}
                      {pet.drug_allergies?.[0] || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        อาหารที่แพ้:
                      </Typography>{" "}
                      {pet.food_allergies?.[0] || "-"}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        โรคประจำตัว:
                      </Typography>{" "}
                      {pet.diseases?.[0] || "-"}
                    </Grid>
                    {/* <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: "bold", display: "inline" }}
                      >
                        เจ้าของ:
                      </Typography>{" "}
                      {pet.ownerName}
                    </Grid> */}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {/* ฟอร์มวัคซีน */}
            {vaccineList.map((item, index) => (
              <Paper key={index} sx={{ p: 2, mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  รายละเอียดวัคซีน
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="วันที่ได้รับวัคซีน"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={item.date}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        const date = e.target.value;

                        newList[index].date = date;

                        // หา effective_days จาก vaccine ที่เลือก
                        const selectedVaccine = vaccine_dropdown.find(
                          (v) => v.value === newList[index].vaccineName,
                        );

                        if (selectedVaccine?.context?.effective_days) {
                          newList[index].nextDate = addDays(
                            date,
                            selectedVaccine.context.effective_days,
                          );
                        }

                        setVaccineList(newList);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ mb: 1 }}>
                      แนบรูป Sticker Vaccine*
                    </Typography>

                    <Box
                      onClick={() => {
                        setCameraIndex(index);
                        setOpenCamera(true);
                      }}
                      sx={{
                        border: "2px dashed #ccc",
                        p: 4,
                        textAlign: "center",
                        borderRadius: 3,
                        cursor: "pointer",
                        transition: "0.2s",

                        "&:hover": {
                          borderColor: "#1976d2",
                          backgroundColor: "#f8fbff",
                        },
                      }}
                    >
                      <PhotoCamera
                        sx={{
                          fontSize: 56,
                          color: "#777",
                        }}
                      />

                      <Typography
                        variant="body1"
                        sx={{
                          mt: 2,
                          fontWeight: 600,
                        }}
                      >
                        ถ่ายรูป หรือ เลือกจากแฟ้ม
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: "#888",
                        }}
                      >
                        รองรับ Webcam และรูปจากเครื่อง
                      </Typography>

                      {item.image && (
                        <Box sx={{ mt: 3 }}>
                          <img
                            src={
                              typeof item.image === "string"
                                ? item.image.startsWith("data:image")
                                  ? item.image
                                  : `data:image/jpeg;base64,${item.image}`
                                : URL.createObjectURL(item.image)
                            }
                            alt="preview"
                            style={{
                              width: 140,
                              height: 140,
                              objectFit: "cover",
                              borderRadius: 14,
                              border: "1px solid #ddd",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="ชื่อวัคซีน / brand name vaccine"
                      fullWidth
                      value={item.vaccineName}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        const vaccineName = e.target.value;

                        newList[index].vaccineName = vaccineName;

                        const selectedVaccine = vaccine_dropdown.find(
                          (v) => v.value === vaccineName,
                        );

                        if (
                          newList[index].date &&
                          selectedVaccine?.context?.effective_days
                        ) {
                          newList[index].nextDate = addDays(
                            newList[index].date,
                            selectedVaccine.context.effective_days,
                          );
                        }

                        setVaccineList(newList);
                      }}
                    >
                      {vaccine_dropdown.map((v) => (
                        <MenuItem key={v.value} value={v.value}>
                          <img
                            src={v?.context?.photo}
                            alt={v.label}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                          />
                          {v.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Lot No."
                      fullWidth
                      value={item.lotNo}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].lotNo = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="วันหมดอายุ / EXP date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      value={item.expDate}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].expDate = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="วันที่นัดครั้งถัดไป"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={item.nextDate}
                      onChange={(e) => {
                        const newList = [...vaccineList];
                        newList[index].nextDate = e.target.value;
                        setVaccineList(newList);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mt: 1, color: "#000000" }}>
                      วันที่ครบระยะเวลาป้องกัน
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: "#888" }}>
                      อ้างอิงจากวันที่ได้รับยา โดยหลังจากนี้ควรได้รับยาอีกครั้ง
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, color: "#ee820e" }}>
                      {item.nextDate}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={addVaccineList}
              sx={{ mb: 4 }}
            >
              เพิ่มรายการ
            </Button> */}

            {/* ฟอร์มหมอ */}
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
              สัตวแพทย์
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="สัตวแพทย์ผู้ให้บริการ"
                  value={`${vet.name} ${vet.lastname}`}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="เลขที่ใบอนุญาต"
                  value={vet.license_no}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ชื่อสถานพยาบาล"
                  value={vet.hospital}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ข้อมูลเพิ่มเติม"
                  value={vet.note}
                  onChange={(e) =>
                    setVet((prev) => ({ ...prev, note: e.target.value }))
                  }
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={saveVaccine}
              >
                บันทึกการฉีดวัคซีน
              </Button>
            </Box>
            <Box sx={{ height: 50 }} />
          </>
        )}
        <Dialog
          open={openQrDialog}
          onClose={() => setOpenQrDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>สแกน QR Code</DialogTitle>
          <DialogContent
            sx={{ display: "flex", justifyContent: "center", p: 2 }}
          >
            {openQrDialog && (
              <QrReader
                delay={300}
                resolution={640}
                qrArea={null}
                constraints={{ video: { facingMode: "environment" } }}
                onError={(err) => console.error(err)}
                onScan={(data) => {
                  if (data) {
                    const text =
                      data.text || (typeof data === "string" ? data : null);
                    if (text) {
                      setOpenQrDialog(false);
                      searchPet(text);
                    }
                  }
                }}
                style={{ width: "100%", maxWidth: "400px" }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenQrDialog(false)}>ปิด</Button>
          </DialogActions>
        </Dialog>
        {/* เปิดกล้อง */}

        <Dialog
          open={openCamera}
          onClose={() => setOpenCamera(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            ถ่ายรูป Vaccine Sticker
            <IconButton onClick={() => setOpenCamera(false)}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 2,
              }}
            >
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                style={{
                  width: "100%",
                  maxWidth: 420,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
                videoConstraints={{
                  facingMode: { ideal: "environment" },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 3,
                }}
              >
                {/* ถ่ายรูป */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={captureImage}
                  startIcon={<PhotoCamera />}
                >
                  ถ่ายรูป
                </Button>

                {/* เลือกจากแฟ้ม */}
                <Button
                  component="label"
                  variant="outlined"
                  size="large"
                  startIcon={<Folder />}
                >
                  เลือกจากแฟ้ม
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      if (file && cameraIndex !== null) {
                        const newList = [...vaccineList];
                        newList[cameraIndex].image = file;
                        setVaccineList(newList);
                      }

                      setOpenCamera(false);
                    }}
                  />
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default ServiceVaccinePage;
